/**
 * Pure helpers for the Admin → AI Skills page: filtering, status → actions,
 * scope labels, evidence text and the editor draft (dirty tracking and
 * serialisation). No React, no API — everything here is unit tested.
 */

// `deprecated` is the stored status; the page calls it Retired.
export const STATUSES = [
  { value: 'proposed', label: 'Proposed', tone: 'warn' },
  { value: 'approved', label: 'Approved', tone: 'ok' },
  { value: 'rejected', label: 'Rejected', tone: 'bad' },
  { value: 'deprecated', label: 'Retired', tone: 'off' },
];
export const STATUS_VALUES = STATUSES.map((s) => s.value);

export function statusMeta(status) {
  return STATUSES.find((s) => s.value === status) || { value: status, label: status || 'Unknown', tone: 'off' };
}

// Kinds the store accepts (azfApi/ai/skills/skillRepo.js buildSkill).
export const KINDS = [
  { value: 'procedure', label: 'Procedure', hint: 'A how-to the assistant follows' },
  { value: 'tool_gap', label: 'Tool gap', hint: 'Engineering note: a tool is missing or wrong' },
  { value: 'data_issue', label: 'Data issue', hint: 'Data note: a mapping or feed is wrong' },
];

export function kindLabel(kind) {
  const k = KINDS.find((x) => x.value === kind);
  if (k) return k.label;
  if (!kind) return 'Procedure';
  const s = String(kind).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const EMPTY_COPY = {
  proposed: { title: 'No proposed skills', body: 'The assistant proposes these after reflecting on conversations where it wasted tool calls or an answer was downvoted.' },
  approved: { title: 'No approved skills', body: 'Approve a proposed skill and the assistant starts using it on matching questions.' },
  rejected: { title: 'No rejected skills', body: 'Skills you reject stay here so you can see what the assistant suggested.' },
  deprecated: { title: 'No retired skills', body: 'Retire an approved skill to stop the assistant using it without deleting it.' },
};

// ── Sorting, counting, filtering ─────────────────────────────────────────────

function ts(v) {
  const t = new Date(v || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Newest first by creation (updatedAt moves on every usage counter bump). */
export function sortNewest(skills) {
  return [...(skills || [])].sort((a, b) => ts(b.createdAt) - ts(a.createdAt) || String(a.id).localeCompare(String(b.id)));
}

export function countByStatus(skills) {
  const counts = Object.fromEntries(STATUS_VALUES.map((s) => [s, 0]));
  for (const s of skills || []) if (s && counts[s.status] != null) counts[s.status] += 1;
  return counts;
}

/** Distinct kinds present in the data, in KINDS order, unknown kinds last. */
export function kindOptions(skills) {
  const present = new Set((skills || []).map((s) => s.kind || 'procedure'));
  const known = KINDS.filter((k) => present.has(k.value)).map((k) => ({ value: k.value, label: k.label }));
  const extra = [...present].filter((k) => !KINDS.some((x) => x.value === k)).sort()
    .map((k) => ({ value: k, label: kindLabel(k) }));
  return [...known, ...extra];
}

export function skillSearchText(skill) {
  const t = skill?.triggers || {};
  return [
    skill?.title, skill?.summary, skill?.body,
    ...(t.phrases || []), ...(t.intents || []), ...(t.tools || []), ...(t.workflows || []),
  ].filter(Boolean).join('\n').toLowerCase();
}

/**
 * status: one status value (or null for all); query: free text matched
 * against title, summary, body and every trigger list (all words must hit);
 * kinds: [] = any.
 */
export function filterSkills(skills, { status = null, query = '', kinds = [] } = {}) {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  const kindSet = new Set((kinds || []).map(String));
  return sortNewest(skills).filter((s) => {
    if (status && s.status !== status) return false;
    if (kindSet.size && !kindSet.has(String(s.kind || 'procedure'))) return false;
    if (!words.length) return true;
    const hay = skillSearchText(s);
    return words.every((w) => hay.includes(w));
  });
}

/** Next id for ↑/↓ in the list. Wraps are clamped, not cycled. */
export function moveSelection(ids, currentId, delta) {
  if (!ids.length) return null;
  const i = ids.indexOf(currentId);
  if (i === -1) return delta > 0 ? ids[0] : ids[ids.length - 1];
  return ids[Math.min(ids.length - 1, Math.max(0, i + delta))];
}

/** After a skill leaves the list (approved, deleted …): the next row, else the previous, else none. */
export function neighbourId(ids, id) {
  const i = ids.indexOf(id);
  if (i === -1) return null;
  return ids[i + 1] ?? ids[i - 1] ?? null;
}

// ── Status → actions ─────────────────────────────────────────────────────────

/**
 * The footer buttons for a status, left to right. `to` is the status the
 * action moves the skill to; delete and edit have no target.
 */
export function actionsForStatus(status) {
  switch (status) {
    case 'proposed':
      return [
        { key: 'edit', label: 'Edit' },
        { key: 'reject', label: 'Reject', to: 'rejected', variant: 'danger' },
        { key: 'approve', label: 'Approve', to: 'approved', variant: 'primary' },
      ];
    case 'approved':
      return [
        { key: 'edit', label: 'Edit' },
        { key: 'retire', label: 'Retire', to: 'deprecated' },
      ];
    case 'rejected':
      return [
        { key: 'edit', label: 'Edit' },
        { key: 'repropose', label: 'Move back to proposed', to: 'proposed' },
        { key: 'delete', label: 'Delete', variant: 'danger' },
      ];
    case 'deprecated':
      return [
        { key: 'reapprove', label: 'Re-approve', to: 'approved', variant: 'primary' },
        { key: 'delete', label: 'Delete', variant: 'danger' },
      ];
    default:
      return [{ key: 'edit', label: 'Edit' }];
  }
}

/** Footer helper text: what the primary action will do, in scope terms. */
export function footerHelp(skill, lookups) {
  const where = scopeLabel(skill?.scope, lookups, 'phrase');
  const note = skill?.kind && skill.kind !== 'procedure' ? ' It is a note, not a procedure — prefer fixing the source.' : '';
  switch (skill?.status) {
    case 'proposed':
      return `Approving adds this playbook to the assistant's instructions for ${where}.${note}`;
    case 'approved':
      return `The assistant uses this playbook for ${where}. Retiring stops that and keeps the skill here.`;
    case 'rejected':
      return 'The assistant never sees rejected skills. Move it back to proposed to review it again.';
    case 'deprecated':
      return `Retired — the assistant no longer uses it. Re-approving puts it back in the instructions for ${where}.`;
    default:
      return '';
  }
}

export const ACTION_SUCCESS = {
  approve: 'Approved — the assistant will use this on matching questions',
  reject: 'Rejected',
  retire: 'Retired — the assistant no longer uses this',
  repropose: 'Moved back to proposed',
  reapprove: 'Re-approved — the assistant will use this again',
};

// ── Scope ────────────────────────────────────────────────────────────────────

/**
 * lookups: { orgs: Map|object id→name, locations: Map|object id→{ name, orgId } }
 * Returns names when known; a clearly-labelled fallback otherwise.
 */
function lookup(table, id) {
  if (id == null || !table) return undefined;
  if (table instanceof Map) return table.get(Number(id)) ?? table.get(String(id));
  return table[id] ?? table[String(id)];
}

export function orgName(lookups, orgId) {
  const v = lookup(lookups?.orgs, orgId);
  return (typeof v === 'string' ? v : v?.name) || null;
}

export function locationName(lookups, locationId) {
  const v = lookup(lookups?.locations, locationId);
  return (typeof v === 'string' ? v : v?.name) || null;
}

/**
 * form 'short' (list meta): "All orgs" | "<org>" | "<location>"
 * form 'long' (provenance line): "Applies to all organizations" | "Applies to <org>" | "Applies to <location> (<org>)"
 * form 'phrase' (footer help): "every organization" | "<org>" | "<location>"
 */
export function scopeLabel(scope, lookups, form = 'short') {
  const type = scope?.type || 'global';
  if (type === 'org' && scope.orgId != null) {
    const name = orgName(lookups, scope.orgId) || `organization #${scope.orgId}`;
    return form === 'long' ? `Applies to ${name}` : name;
  }
  if (type === 'location' && scope.locationId != null) {
    const loc = locationName(lookups, scope.locationId) || `property #${scope.locationId}`;
    const locOrgId = scope.orgId ?? lookup(lookups?.locations, scope.locationId)?.orgId;
    const org = locOrgId != null ? orgName(lookups, locOrgId) : null;
    if (form === 'long') return `Applies to ${loc}${org ? ` (${org})` : ''}`;
    return loc;
  }
  if (form === 'long') return 'Applies to all organizations';
  if (form === 'phrase') return 'every organization';
  return 'All orgs';
}

// ── Provenance + evidence ────────────────────────────────────────────────────

export function shortDate(value) {
  const t = ts(value);
  if (!t) return '';
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
}

export function exactDate(value) {
  const t = ts(value);
  return t ? new Date(t).toLocaleString() : '';
}

const TRIGGER_TEXT = {
  turn: 'after reflecting on a conversation',
  downvote: 'after a downvoted answer',
  manual: 'after a manual reflection',
};

export function authorText(skill) {
  const p = skill?.provenance || {};
  if ((p.author || 'model') === 'model') {
    return `Proposed by the assistant ${TRIGGER_TEXT[p.trigger] || 'after reflecting on a conversation'}`;
  }
  return p.userId ? `Written by ${p.userId}` : 'Written by an admin';
}

/** Chat sessions are partitioned by user, so only the owner's link opens. */
export function canOpenThread(ownerId, viewerId) {
  return Boolean(ownerId && viewerId && String(ownerId).toLowerCase() === String(viewerId).toLowerCase());
}

export function conversationCount(skill) {
  return skill?.provenance?.threadId ? 1 : 0;
}

export function plural(n, one, many) {
  return `${n} ${n === 1 ? one : (many || `${one}s`)}`;
}

/** "All orgs · 1 conversation · Sep 22" */
export function metaLine(skill, lookups) {
  const n = conversationCount(skill);
  const middle = n ? plural(n, 'conversation') : ((skill?.provenance?.author || 'model') === 'model' ? 'No conversation' : 'Written by hand');
  return [scopeLabel(skill?.scope, lookups, 'short'), middle, shortDate(skill?.createdAt)].filter(Boolean).join(' · ');
}

/**
 * Evidence bits from the reflection signals — the proposal's own snapshot
 * first, else the joined reflection row. Only non-zero signals are listed.
 */
export function evidenceParts(skill, reflection) {
  const n = conversationCount(skill);
  const signals = skill?.provenance?.signals || reflection?.signals || null;
  const turns = skill?.provenance?.turnCount ?? reflection?.turnCount ?? null;
  const parts = [];
  if (n) parts.push(plural(n, 'conversation'));
  if (turns) parts.push(plural(turns, 'turn'));
  if (signals) {
    if (signals.toolFailures) parts.push(plural(signals.toolFailures, 'failed tool call'));
    if (signals.verifierRetries) parts.push(plural(signals.verifierRetries, 'verifier retry', 'verifier retries'));
    if (signals.spiralBreakers) parts.push(plural(signals.spiralBreakers, 'loop break'));
  }
  if ((skill?.provenance?.trigger || reflection?.trigger) === 'downvote') parts.push('downvoted');
  return parts;
}

export function signalsText(signals) {
  if (!signals) return '';
  const parts = [];
  if (signals.toolCalls != null) parts.push(plural(signals.toolCalls, 'call'));
  if (signals.toolFailures) parts.push(`${signals.toolFailures} failed`);
  if (signals.maxIterations != null) parts.push(`max ${signals.maxIterations} steps`);
  if (signals.verifierRetries) parts.push(plural(signals.verifierRetries, 'retry', 'retries'));
  if (signals.spiralBreakers) parts.push(plural(signals.spiralBreakers, 'loop break'));
  return parts.join(' · ');
}

export const REFLECTION_TRIGGER_LABEL = { turn: 'After a turn', downvote: 'Downvote', manual: 'Manual' };

export const SKIP_REASON_TEXT = {
  min_turns: 'The conversation is too short to reflect on.',
  clean: 'Nothing went wrong in the conversation, so no model call was made.',
  empty: 'The conversation has no user messages.',
  model_error: 'The model call failed.',
};

/** Decision history for display, oldest first. Legacy rows without history get one synthetic entry. */
export function historyEntries(skill) {
  const list = Array.isArray(skill?.history) ? skill.history.filter(Boolean) : [];
  if (list.length) return list;
  const r = skill?.review;
  if (r?.decidedAt) return [{ at: r.decidedAt, action: 'status', status: skill.status, actor: r.decidedBy, note: r.note }];
  return [];
}

export function historyText(entry) {
  if (!entry) return '';
  if (entry.action === 'created') return `Created as ${statusMeta(entry.status).label.toLowerCase()}`;
  if (entry.action === 'edited') {
    const f = Array.isArray(entry.fields) && entry.fields.length ? ` (${entry.fields.join(', ')})` : '';
    return `Edited${entry.version ? ` to v${entry.version}` : ''}${f}`;
  }
  if (entry.action === 'status') {
    const to = statusMeta(entry.status).label.toLowerCase();
    return entry.from ? `${statusMeta(entry.from).label} → ${to}` : `Marked ${to}`;
  }
  return entry.action || '';
}

export function actorText(actor) {
  if (!actor) return 'unknown';
  return actor === 'model' ? 'the assistant' : actor;
}

// ── Editor draft ─────────────────────────────────────────────────────────────

export const LIMITS = { title: 160, summary: 400, body: 6000, tags: 24 };

export function emptyDraft() {
  return {
    title: '', kind: 'procedure', summary: '', body: '',
    scopeType: 'global', orgId: null, locationId: null,
    phrases: [], intents: [], tools: [], workflows: [],
  };
}

function numOrNull(v) {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}

export function draftFromSkill(skill) {
  if (!skill) return emptyDraft();
  const t = skill.triggers || {};
  const scope = skill.scope || {};
  return {
    title: skill.title || '',
    kind: skill.kind || 'procedure',
    summary: skill.summary || '',
    body: skill.body || '',
    scopeType: ['org', 'location'].includes(scope.type) ? scope.type : 'global',
    orgId: numOrNull(scope.orgId),
    locationId: numOrNull(scope.locationId),
    phrases: [...(t.phrases || [])],
    intents: [...(t.intents || [])],
    tools: [...(t.tools || [])],
    workflows: [...(t.workflows || [])],
  };
}

/** Same normalisation the store applies (trim, lower-case, dedupe, cap). */
export function normalizeTags(list) {
  return [...new Set((list || []).map((v) => String(v || '').trim().toLowerCase()).filter(Boolean))].slice(0, LIMITS.tags);
}

/** Split typed/pasted text into tags on commas and newlines. */
export function splitTagInput(text) {
  return String(text || '').split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
}

export function serializeDraft(draft) {
  const d = draft || emptyDraft();
  let scope = { type: 'global', orgId: null, locationId: null };
  if (d.scopeType === 'org') scope = { type: 'org', orgId: numOrNull(d.orgId), locationId: null };
  if (d.scopeType === 'location') scope = { type: 'location', orgId: numOrNull(d.orgId), locationId: numOrNull(d.locationId) };
  return {
    title: String(d.title || '').trim(),
    kind: d.kind || 'procedure',
    summary: String(d.summary || '').trim(),
    body: String(d.body || '').trim(),
    scope,
    triggers: {
      phrases: normalizeTags(d.phrases),
      intents: normalizeTags(d.intents),
      tools: normalizeTags(d.tools),
      workflows: normalizeTags(d.workflows),
    },
  };
}

const DIRTY_FIELDS = [
  ['title', (p) => p.title],
  ['kind', (p) => p.kind],
  ['summary', (p) => p.summary],
  ['body', (p) => p.body],
  ['scope', (p) => p.scope],
  ['phrases', (p) => p.triggers.phrases],
  ['intents', (p) => p.triggers.intents],
  ['tools', (p) => p.triggers.tools],
  ['workflows', (p) => p.triggers.workflows],
];

/** Changed field names between a draft and its starting point (both drafts). */
export function dirtyFields(draft, initial) {
  const a = serializeDraft(draft);
  const b = serializeDraft(initial);
  return DIRTY_FIELDS.filter(([, get]) => JSON.stringify(get(a)) !== JSON.stringify(get(b))).map(([k]) => k);
}

export function validateDraft(draft) {
  const p = serializeDraft(draft);
  const errors = {};
  if (!p.title) errors.title = 'Give the skill a title.';
  else if (p.title.length > LIMITS.title) errors.title = `Keep the title under ${LIMITS.title} characters.`;
  if (p.summary.length > LIMITS.summary) errors.summary = `Keep the summary under ${LIMITS.summary} characters.`;
  if (!p.body) errors.body = 'The playbook is what the assistant reads — it cannot be empty.';
  else if (p.body.length > LIMITS.body) errors.body = `The playbook is limited to ${LIMITS.body.toLocaleString()} characters.`;
  if (p.scope.type === 'org' && p.scope.orgId == null) errors.scope = 'Pick an organization.';
  if (p.scope.type === 'location' && p.scope.locationId == null) errors.scope = 'Pick a property.';
  return errors;
}

export function errorMessage(err, fallback) {
  return err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
}
